import { Store } from '@tanstack/react-store'

import { type BuilderStore, type UpdateChatStateBuilder, TeacherFocusBuilder } from './builder-store.types'

import { type CardActivity, CardActivityType } from '@/models/card-activity/card-activity.model'

const respondCardGroup = [
  CardActivityType.RESPOND,
  CardActivityType.READ_RESPOND,
  CardActivityType.RESPOND_WRITE,
  CardActivityType.FREE_RESPONSE,
]

const initialAssistantMessage = `## Hi [Teacher’s Name]!

I can help you create multiple pages of any type - all at once! Just tell me what you need.`

function getDefaultFieldsForType(cardType: CardActivityType): Partial<CardActivity> {
  switch (cardType) {
    case CardActivityType.RESPOND:
    case CardActivityType.READ_RESPOND:
    case CardActivityType.RESPOND_WRITE:
    case CardActivityType.FREE_RESPONSE:
      return {
        prompt: '',
        promptType: 'text',
        audio: { url: null, path: null },
        grading_method: 'manual',
        scoring_type: 'manual',
        rubricId: null,
        grading_criteria: '',
      }

    case CardActivityType.MULTIPLE_CHOICE:
      return {
        MCQType: 'single',
        answer: [],
        choices: [],
        question: '',
      }

    case CardActivityType.REPEAT:
    case CardActivityType.READ_REPEAT:
      return {
        audio: { url: null, path: null },
        target_text: '',
        language: null,
        difficulty: 'normal',
      }

    default:
      return {}
  }
}

import { type CardActivityWithId } from '@/models/card-activity/card-activity.model'
import { checkActiveTeacherFocus } from '@/views/NewBuilder/utils/check-active-teacher-focus'
import { type BuilderVideoType } from '@/views/NewBuilder/services/builder-videos/get-builder-videos.service'

export const builderStore = new Store<BuilderStore, (cb: BuilderStore) => BuilderStore>({
  layout: 'grid',
  isSubmitting: false,
  isEditable: true,
  chatState: {
    messages: [
      {
        role: 'assistant',
        content: initialAssistantMessage,
        isInitialMessage: true,
      },
    ],
    threadId: null,
    currentRunId: null,
    isLoading: false,
    isWaiting: false,
  },
  cardScores: {},
  activeScreen: 'content',
  importModalOpen: false,
  showRubricDialog: false,
  teacherFocus: TeacherFocusBuilder.GENERAL,
  isFormOpen: false,
  isSettingsOpen: false,
  isAIBuilderOpen: false,
  isMediaAreaOpen: false,
  showWarningMissingGradingCriteria: null,
  showDialogGradingCriteria: false,
  showBuilderVideoDialog: {
    isOpen: false,
    type: null,
  },
  cardFormData: {
    type: CardActivityType.READ_RESPOND,
    prompt: '',
    promptType: 'text',
    audio: { url: null, path: null },
    grading_method: 'manual',
    scoring_type: 'manual',
    rubricId: null,
    grading_criteria: '',
  },
})

export const updateBuilderStore = (newState: Partial<BuilderStore>) => {
  builderStore.setState(state => {
    return {
      ...state,
      ...newState,
    }
  })
}

export const updateChatState = (data: UpdateChatStateBuilder) => {
  builderStore.setState(state => {
    const updatedMessages = data.messages ? [...state.chatState.messages, ...data.messages] : state.chatState.messages

    return {
      ...state,
      chatState: {
        ...state.chatState,
        ...data,
        messages: updatedMessages,
      },
    }
  })
}

export const updateCardScores = (data: Partial<BuilderStore>) => {
  builderStore.setState(state => {
    return {
      ...state,
      cardScores: {
        ...state.cardScores,
        ...data,
      },
    }
  })
}

export const clearChatState = () => {
  builderStore.setState(state => {
    return {
      ...state,
      chatState: {
        messages: [
          {
            role: 'assistant',
            content: initialAssistantMessage,
            isInitialMessage: true,
          },
        ],
        threadId: null,
      },
    }
  })
}

export const clearCardScores = () => {
  builderStore.setState(state => {
    return {
      ...state,
      cardScores: {},
    }
  })
}

export function updateTeacherFocusBuilder(newFocus: TeacherFocusBuilder, card: CardActivityWithId | undefined) {
  const isActiveTeacherFocus = checkActiveTeacherFocus(card, newFocus)

  if (isActiveTeacherFocus) {
    builderStore.setState(state => {
      return {
        ...state,
        teacherFocus: newFocus,
      }
    })
  } else {
    builderStore.setState(state => {
      return {
        ...state,
        teacherFocus: TeacherFocusBuilder.GENERAL,
      }
    })
  }
}

export function updateCardType(newType: CardActivityType) {
  builderStore.setState(state => {
    const oldType = state.cardFormData.type
    const bothTextResponse = respondCardGroup.includes(oldType) && respondCardGroup.includes(newType)

    if (bothTextResponse) {
      return {
        ...state,
        cardFormData: {
          ...state.cardFormData,
          type: newType,
        },
      }
    }

    return {
      ...state,
      cardFormData: {
        type: newType,
        ...getDefaultFieldsForType(newType),
      },
    }
  })
}

export function updateCardField<K extends keyof CardActivity>(field: K | `${string}.${string}`, value: unknown) {
  builderStore.setState(state => {
    if (typeof field === 'string' && field.includes('.')) {
      const [parent, child] = field.split('.') as [keyof CardActivity, string]

      if (state.cardFormData[parent] && typeof state.cardFormData[parent] === 'object') {
        return {
          ...state,
          cardFormData: {
            ...state.cardFormData,
            [parent]: {
              ...(state.cardFormData[parent] as Record<string, unknown>),
              [child]: value,
            },
          },
        }
      }
    }

    return {
      ...state,
      cardFormData: {
        ...state.cardFormData,
        [field]: value as CardActivity[K],
      },
    }
  })
}

export function updateCardFields(fields: Partial<CardActivity>) {
  builderStore.setState(state => {
    const updated = { ...state.cardFormData, ...fields }

    if (fields.audio) {
      updated.audio = {
        ...state.cardFormData.audio,
        ...fields.audio,
      }
    }

    return {
      ...state,
      cardFormData: updated,
    }
  })
}

export function clearCardFormData() {
  builderStore.setState(state => {
    return {
      ...state,
      cardFormData: {
        type: CardActivityType.READ_RESPOND,
        ...getDefaultFieldsForType(CardActivityType.READ_RESPOND),
      },
    }
  })
}

export function updateMediaAreaOpen(newState: boolean) {
  builderStore.setState(state => {
    return {
      ...state,
      isMediaAreaOpen: newState,
    }
  })
}

const updateShowWarningMissingGradingCriteria = (newState: BuilderStore['showWarningMissingGradingCriteria']) => {
  builderStore.setState(state => {
    return {
      ...state,
      showWarningMissingGradingCriteria: newState,
    }
  })
}

const updateShowDialogGradingCriteria = (newState: boolean) => {
  builderStore.setState(state => {
    return {
      ...state,
      showDialogGradingCriteria: newState,
    }
  })
}

const toggleBuilderVideoDialog = (type?: BuilderVideoType) => {
  builderStore.setState(state => {
    return {
      ...state,
      showBuilderVideoDialog: {
        type: type ?? null,
        isOpen: !state.showBuilderVideoDialog.isOpen,
      },
    }
  })
}

export const builderStoreActions = {
  updateBuilderStore,
  updateCardScores,
  clearCardScores,
  updateChatState,
  clearChatState,
  updateTeacherFocusBuilder,
  updateCardType,
  updateCardField,
  updateCardFields,
  clearCardFormData,
  updateMediaAreaOpen,
  updateShowWarningMissingGradingCriteria,
  updateShowDialogGradingCriteria,
  toggleBuilderVideoDialog,
}
